import { __awaiter } from '../_virtual/_tslib.js';
import { WalletConnectorBase } from '@dynamic-labs/wallet-connector-core';
import { CancellablePromise } from '@dynamic-labs/utils';
import { logger } from './utils/logger/logger.js';

class MagicWalletConnectorBase extends WalletConnectorBase {
    constructor(props) {
        super(props);
        this.canConnectViaCustodialService = true;
        this.isEmbeddedWallet = true;
        this.walletUiUtils = props.walletUiUtils;
        this.providerProps = this.prepareProps(props);
    }
    get storedAddressKey() {
        return `${MagicWalletConnectorBase.storagePrefix}:${this.chain}:address`;
    }
    get chain() {
        switch (this.providerProps.chain) {
            case 'flow':
                return 'FLOW';
            case 'solana':
                return 'SOL';
            case 'eip155':
            default:
                return 'EVM';
        }
    }
    get chainId() {
        var _a;
        return (_a = this.providerProps.chainId) !== null && _a !== void 0 ? _a : this.providerProps.defaultChainId;
    }
    getUserMetadata() {
        return __awaiter(this, void 0, void 0, function* () {
            if (yield this.isLoggedIn()) {
                const client = this.magicClient.getClient();
                return client.user.getInfo();
            }
            return undefined;
        });
    }
    isLoggedIn() {
        const client = this.magicClient.getClient();
        return client.user.isLoggedIn();
    }
    getAndCacheWalletAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            const client = this.magicClient.getClient();
            const metadata = yield client.user.getInfo();
            if (!metadata || !metadata.publicAddress) {
                throw new Error('No public address');
            }
            if (metadata.issuer) {
                this.providerResources = [`magic-auth:issuer:${metadata.issuer}`];
            }
            localStorage.setItem(this.storedAddressKey, metadata.publicAddress);
            return metadata.publicAddress;
        });
    }
    prepareProps(props) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const apiKey = (_b = (_a = props.apiProviders) === null || _a === void 0 ? void 0 : _a.magicLink) === null || _b === void 0 ? void 0 : _b.providerProjectId;
        if (!apiKey) {
            throw new Error('Missing MagicLink configuration. Add your MagicLink API key to your project configuration via the Dynamic Labs dashboard.');
        }
        const defaultChainId = (_d = (_c = props.apiProviders) === null || _c === void 0 ? void 0 : _c.magicLink) === null || _d === void 0 ? void 0 : _d.defaultChainId;
        const [chain, chainId] = (_h = (_g = (_f = (_e = props.apiProviders) === null || _e === void 0 ? void 0 : _e.magicLink) === null || _f === void 0 ? void 0 : _f.defaultChain) === null || _g === void 0 ? void 0 : _g.split(':')) !== null && _h !== void 0 ? _h : [];
        return {
            apiKey,
            chain,
            chainId,
            defaultChainId,
        };
    }
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            localStorage.removeItem(this.storedAddressKey);
            const client = this.magicClient.getClient();
            yield client.user.logout();
        });
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.fetchPublicAddress();
        });
    }
    getConnectedAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            const address = localStorage.getItem(this.storedAddressKey);
            if (!address)
                return [];
            return [address];
        });
    }
    getDeepLink() {
        return undefined;
    }
    fetchPublicAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            const address = localStorage.getItem(this.storedAddressKey);
            if (address)
                return address;
            try {
                this._currentAuthCancellablePromise = CancellablePromise.fromPromise(this.loginWithMagic());
                this._currentAuthCancellablePromise.onCancel(() => {
                    if ('cancelLogIn' in this)
                        this.cancelLogIn();
                });
                yield this._currentAuthCancellablePromise;
                return this.getAndCacheWalletAddress();
            }
            catch (err) {
                // If we don't throw this error, useConnectWallet throws "missing-public-address" later
                // "missing-public-address" displays a UI message we don't need in this scenario
                if (err === null || err === void 0 ? void 0 : err.wasCancelled) {
                    const error = new Error();
                    // Don't show an error message
                    error.code = 'user-cancelled';
                    throw error;
                }
                this.endSession();
                logger.error(err);
                return;
            }
        });
    }
}
MagicWalletConnectorBase.storagePrefix = 'dynamic:magic';

export { MagicWalletConnectorBase };
